import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { foyerApi } from "../http/foyer";
import {
  DEFAULT_ALERT_OFFLINE_MINUTES,
  DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS,
  DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS,
  DEFAULT_PHOTO_RETENTION_MONTHS,
  DEFAULT_METERED_PASS_THRESHOLD_PERCENT,
  DEFAULT_METERED_FLEET_GOAL_PERCENT,
  DEFAULT_TV_STREAMING_SLUG,
  DEFAULT_CHANNEL_LOGO_OFFSET_X,
  DEFAULT_CHANNEL_LOGO_OFFSET_Y,
  DEFAULT_CHANNEL_LOGO_WIDTH,
} from "@shared/types/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { cornerEdges, ruleFor } from "@shared/tv-corners";
import HelpBadge from "../components/HelpBadge";

/**
 * This card DESCRIBES a corner it does not own (`CONTENT-26`).
 *
 * The corner belongs to `@shared/tv-corners`, so both labels and the help text
 * are derived from it rather than typed here. That is the whole point: when the
 * logo moved bottom-right -> bottom-left on 2026-09-08 (`d1fbd94`), the corner
 * had to be hand-chased across four files including this one, and a stale
 * "Margin from right" would have had an operator adjusting the wrong edge with
 * nothing to catch it.
 */
const LOGO_CORNER = ruleFor("channel-logo").corner;
const LOGO_EDGES = cornerEdges(LOGO_CORNER);

/** Split a free-text recipients box (newline/comma separated) into emails. */
function parseRecipients(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

type DurationUnit = "minutes" | "hours" | "days";
const UNIT_MINUTES: Record<DurationUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
};

/** Present a minutes value in the largest whole unit (90 → 90 min, 120 → 2 h). */
function splitMinutes(min: number): { value: number; unit: DurationUnit } {
  if (min >= 1440 && min % 1440 === 0)
    return { value: min / 1440, unit: "days" };
  if (min >= 60 && min % 60 === 0) return { value: min / 60, unit: "hours" };
  return { value: min, unit: "minutes" };
}

export default function GeneralSettingsPage() {
  const [slug, setSlug] = useState("");
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [recipientsHotel, setRecipientsHotel] = useState("");
  const [recipientsCare, setRecipientsCare] = useState("");
  const [thresholdValue, setThresholdValue] = useState("1");
  const [thresholdUnit, setThresholdUnit] = useState<DurationUnit>("hours");
  const [onlineWindowSeconds, setOnlineWindowSeconds] = useState(
    String(DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS)
  );
  const [failedCommandHours, setFailedCommandHours] = useState(
    String(DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS)
  );
  const [photoRetentionMonths, setPhotoRetentionMonths] = useState(
    String(DEFAULT_PHOTO_RETENTION_MONTHS)
  );
  const [logoOffsetX, setLogoOffsetX] = useState(
    String(DEFAULT_CHANNEL_LOGO_OFFSET_X)
  );
  const [logoOffsetY, setLogoOffsetY] = useState(
    String(DEFAULT_CHANNEL_LOGO_OFFSET_Y)
  );
  const [logoWidth, setLogoWidth] = useState(
    String(DEFAULT_CHANNEL_LOGO_WIDTH)
  );
  const [meteredPass, setMeteredPass] = useState(
    String(DEFAULT_METERED_PASS_THRESHOLD_PERCENT)
  );
  const [meteredGoal, setMeteredGoal] = useState(
    String(DEFAULT_METERED_FLEET_GOAL_PERCENT)
  );
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    foyerApi
      .getSettings()
      .then((s) => {
        setSlug(s.tvStreamingAppSlug);
        setAlertsEnabled(s.alertsEnabled);
        setRecipientsHotel(s.alertRecipientsHospitality.join("\n"));
        setRecipientsCare(s.alertRecipientsCare.join("\n"));
        const { value, unit } = splitMinutes(s.alertOfflineMinutes);
        setThresholdValue(String(value));
        setThresholdUnit(unit);
        setOnlineWindowSeconds(String(s.healthOnlineWindowSeconds));
        setFailedCommandHours(String(s.healthFailedCommandWindowHours));
        setPhotoRetentionMonths(String(s.photoRetentionMonths));
        setLogoOffsetX(String(s.channelLogoOffsetX));
        setLogoOffsetY(String(s.channelLogoOffsetY));
        setLogoWidth(String(s.channelLogoWidth));
        setMeteredPass(String(s.meteredPassThresholdPercent));
        setMeteredGoal(String(s.meteredFleetGoalPercent));
        setLoaded(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function save() {
    setSaving(true);
    setToast(null);
    setError(null);
    try {
      const minutes =
        (Number(thresholdValue) || 0) * UNIT_MINUTES[thresholdUnit] ||
        DEFAULT_ALERT_OFFLINE_MINUTES;
      const next = await foyerApi.updateSettings({
        tvStreamingAppSlug: slug,
        alertsEnabled,
        alertRecipientsHospitality: parseRecipients(recipientsHotel),
        alertRecipientsCare: parseRecipients(recipientsCare),
        alertOfflineMinutes: minutes,
        healthOnlineWindowSeconds:
          Number(onlineWindowSeconds) || DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS,
        healthFailedCommandWindowHours:
          Number(failedCommandHours) ||
          DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS,
        photoRetentionMonths:
          Number(photoRetentionMonths) || DEFAULT_PHOTO_RETENTION_MONTHS,
        // Not the `|| DEFAULT` idiom used above for X/Y: 0 is a real, valid
        // offset here (flush against the edge), unlike the other fields on
        // this page, where 0 is never a legitimate value. The server clamps
        // regardless, so this only needs to pass a real number through.
        channelLogoOffsetX: Number.isFinite(Number(logoOffsetX))
          ? Number(logoOffsetX)
          : DEFAULT_CHANNEL_LOGO_OFFSET_X,
        channelLogoOffsetY: Number.isFinite(Number(logoOffsetY))
          ? Number(logoOffsetY)
          : DEFAULT_CHANNEL_LOGO_OFFSET_Y,
        channelLogoWidth: Number(logoWidth) || DEFAULT_CHANNEL_LOGO_WIDTH,
        meteredPassThresholdPercent:
          Number(meteredPass) || DEFAULT_METERED_PASS_THRESHOLD_PERCENT,
        meteredFleetGoalPercent:
          Number(meteredGoal) || DEFAULT_METERED_FLEET_GOAL_PERCENT,
      });
      setSlug(next.tvStreamingAppSlug);
      setAlertsEnabled(next.alertsEnabled);
      setRecipientsHotel(next.alertRecipientsHospitality.join("\n"));
      setRecipientsCare(next.alertRecipientsCare.join("\n"));
      const split = splitMinutes(next.alertOfflineMinutes);
      setThresholdValue(String(split.value));
      setThresholdUnit(split.unit);
      setOnlineWindowSeconds(String(next.healthOnlineWindowSeconds));
      setFailedCommandHours(String(next.healthFailedCommandWindowHours));
      setPhotoRetentionMonths(String(next.photoRetentionMonths));
      setLogoOffsetX(String(next.channelLogoOffsetX));
      setLogoOffsetY(String(next.channelLogoOffsetY));
      setLogoWidth(String(next.channelLogoWidth));
      setMeteredPass(String(next.meteredPassThresholdPercent));
      setMeteredGoal(String(next.meteredFleetGoalPercent));
      setToast("Saved");
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const disabled = !loaded || saving;

  return (
    <PageContainer>
      <PageHeader eyebrow="System" title="General settings" />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      <Stack spacing={3}>
        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            {/*
              Android only, and it has to say so (TIZEN-13). A Samsung panel
              has no shell to ask, so `streamingAppVersion` is null there and
              the Fleet view reports nothing - which read as "the vendor app is
              missing" rather than "this platform cannot answer". The version
              half of this promise is the part that does not travel; the slug
              itself still reaches every platform through the app catalogue.
            */}
            <HelpBadge title="Android package the “TV” tile launches and whose installed version the Fleet view reports. Change this if the streaming app is swapped - TVs pick up the new slug on their next config refresh (no reflash needed). The reported version is Android only: a Samsung panel has no shell to ask, so Fleet leaves it blank there rather than calling it missing." />
            <Typography variant="h6" gutterBottom>
              TV streaming app
            </Typography>
            <TextField
              label="Streaming app package slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={DEFAULT_TV_STREAMING_SLUG}
              helperText={`Default: ${DEFAULT_TV_STREAMING_SLUG}`}
              fullWidth
              disabled={disabled}
              inputProps={{
                "data-testid": "settings-streaming-slug",
                spellCheck: false,
              }}
            />
          </CardContent>
        </Card>

        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            <HelpBadge title="The timings behind the green / amber / red premise-health chips. Seen within the online window → green; offline beyond that but under the offline threshold → amber; past the offline threshold (or a recent failed command) → red." />
            <Typography variant="h6" gutterBottom>
              Fleet health thresholds
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
            >
              <TextField
                label="Online window (seconds)"
                type="number"
                value={onlineWindowSeconds}
                onChange={(e) => setOnlineWindowSeconds(e.target.value)}
                helperText={`A device seen within this many seconds counts as online. Default ${DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS}s (min 10s).`}
                disabled={disabled}
                sx={{ flex: "1 1 220px" }}
                inputProps={{
                  min: 10,
                  "data-testid": "settings-online-window",
                }}
              />

              <Stack
                direction="row"
                spacing={1.5}
                sx={{ flex: "1 1 300px", alignItems: "flex-start" }}
              >
                <TextField
                  label="Offline threshold"
                  type="number"
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(e.target.value)}
                  helperText="Offline / unseen longer than this turns the premise red - and triggers alerts."
                  disabled={disabled}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, "data-testid": "settings-alert-value" }}
                />
                <FormControl sx={{ minWidth: 130, mt: 0 }} disabled={disabled}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    label="Unit"
                    value={thresholdUnit}
                    onChange={(e) =>
                      setThresholdUnit(e.target.value as DurationUnit)
                    }
                    data-testid="settings-alert-unit"
                  >
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <TextField
                label="Failed-command window (hours)"
                type="number"
                value={failedCommandHours}
                onChange={(e) => setFailedCommandHours(e.target.value)}
                helperText={`Failed / expired commands within this many hours count against health. Default ${DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS}h (1-168h; commands are kept one week).`}
                disabled={disabled}
                sx={{ flex: "1 1 240px" }}
                inputProps={{
                  min: 1,
                  max: 168,
                  "data-testid": "settings-failed-command-window",
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            <HelpBadge title="Email when a device goes offline or a paired TV never reports in. One digest per check - a premise-wide outage is a single email, not one per TV. Care and hospitality premises notify their own recipients." />
            <Typography variant="h6" gutterBottom>
              Health alerts
            </Typography>
            <Stack spacing={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertsEnabled}
                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                    disabled={disabled}
                    data-testid="settings-alerts-enabled"
                  />
                }
                label="Send health-alert emails"
              />

              <Typography variant="body2" color="text.secondary">
                Alerts fire at the <strong>offline threshold</strong> set under
                Fleet health thresholds above.
              </Typography>

              <TextField
                label="Hospitality recipients"
                value={recipientsHotel}
                onChange={(e) => setRecipientsHotel(e.target.value)}
                placeholder={"ops@nenda.com\noncall@nenda.com"}
                helperText="Alerts for hotel premises. One email per line (or comma-separated)."
                fullWidth
                multiline
                minRows={2}
                disabled={disabled}
                inputProps={{
                  "data-testid": "settings-recipients-hospitality",
                  spellCheck: false,
                }}
              />
              <TextField
                label="Care recipients"
                value={recipientsCare}
                onChange={(e) => setRecipientsCare(e.target.value)}
                placeholder={"care-ops@nenda.com"}
                helperText="Alerts for care premises. One email per line (or comma-separated)."
                fullWidth
                multiline
                minRows={2}
                disabled={disabled}
                inputProps={{
                  "data-testid": "settings-recipients-care",
                  spellCheck: false,
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            <HelpBadge title="How the monthly metered-usage KPI is scored. A premise passes a month only if its signage reached at least the pass line of its own target - it's binary, so a premise at 85% of target counts as a fail. The fleet goal is the share of metered premises that must pass. Closed months keep the pass line they were scored with; a change applies from the running month on." />
            <Typography variant="h6" gutterBottom>
              Metered usage KPI
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
            >
              <TextField
                label="Pass line (% of target)"
                type="number"
                value={meteredPass}
                onChange={(e) => setMeteredPass(e.target.value)}
                helperText={`A premise passes a month at or above this. Default ${DEFAULT_METERED_PASS_THRESHOLD_PERCENT}%.`}
                disabled={disabled}
                sx={{ flex: "1 1 220px" }}
                inputProps={{
                  min: 1,
                  max: 100,
                  "data-testid": "settings-metered-pass-threshold",
                }}
              />
              <TextField
                label="Fleet goal (% of premises passing)"
                type="number"
                value={meteredGoal}
                onChange={(e) => setMeteredGoal(e.target.value)}
                helperText={`The reach KPI is met when at least this share passes. Default ${DEFAULT_METERED_FLEET_GOAL_PERCENT}%.`}
                disabled={disabled}
                sx={{ flex: "1 1 220px" }}
                inputProps={{
                  min: 1,
                  max: 100,
                  "data-testid": "settings-metered-fleet-goal",
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            <HelpBadge title="How long family / activity photos are kept before the hourly cleanup sweep permanently deletes them - both the database record and the stored image file, so nothing is left orphaned. Applies as a global cap on top of each mailbox’s own retention." />
            <Typography variant="h6" gutterBottom>
              Care photo retention
            </Typography>
            <TextField
              label="Retention (months)"
              type="number"
              value={photoRetentionMonths}
              onChange={(e) => setPhotoRetentionMonths(e.target.value)}
              helperText={`Photos older than this are purged on the next sweep. Default ${DEFAULT_PHOTO_RETENTION_MONTHS} months (min 1).`}
              disabled={disabled}
              sx={{ width: 220 }}
              inputProps={{
                min: 1,
                "data-testid": "settings-photo-retention-months",
              }}
            />
          </CardContent>
        </Card>

        <Card sx={{ position: "relative" }}>
          <CardContent sx={{ p: 3 }}>
            <HelpBadge
              title={`Where an effect channel's logo sits on the TV. The corner is granted by the corner authority and is currently ${LOGO_CORNER}; only the margin from it and the rendered width are configurable, and they apply to every channel's logo at once. Set the image itself per channel, from its own edit dialog under Channels.`}
            />
            <Typography variant="h6" gutterBottom>
              Channel logo placement
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
            >
              <TextField
                label={`Margin from ${LOGO_EDGES.x} (px)`}
                type="number"
                value={logoOffsetX}
                onChange={(e) => setLogoOffsetX(e.target.value)}
                helperText={`Default ${DEFAULT_CHANNEL_LOGO_OFFSET_X}px.`}
                disabled={disabled}
                sx={{ flex: "1 1 200px" }}
                inputProps={{ min: 0, "data-testid": "settings-logo-offset-x" }}
              />
              <TextField
                label={`Margin from ${LOGO_EDGES.y} (px)`}
                type="number"
                value={logoOffsetY}
                onChange={(e) => setLogoOffsetY(e.target.value)}
                helperText={`Default ${DEFAULT_CHANNEL_LOGO_OFFSET_Y}px.`}
                disabled={disabled}
                sx={{ flex: "1 1 200px" }}
                inputProps={{ min: 0, "data-testid": "settings-logo-offset-y" }}
              />
              <TextField
                label="Width (px)"
                type="number"
                value={logoWidth}
                onChange={(e) => setLogoWidth(e.target.value)}
                helperText={`Height follows the logo's own aspect ratio. Default ${DEFAULT_CHANNEL_LOGO_WIDTH}px (min 40).`}
                disabled={disabled}
                sx={{ flex: "1 1 200px" }}
                inputProps={{ min: 40, "data-testid": "settings-logo-width" }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1.5, display: "block" }}
            >
              Pixels against a 1920×1080 frame, measured from the bottom-left
              corner - not the top-right.
            </Typography>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            onClick={save}
            disabled={disabled}
            data-testid="settings-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          {toast && (
            <Typography variant="body2" color="text.secondary">
              {toast}
            </Typography>
          )}
        </Stack>
      </Stack>
    </PageContainer>
  );
}
